/**
 * Push Notification Service
 * Handles browser push notifications for alerts
 */

import api from '../utils/api';

class PushNotificationService {
  constructor() {
    this.registration = null;
    this.enabled = false;
    this.listeners = [];
  }

  /**
   * Initialize push notifications
   * Register service worker and request permission
   */
  async initialize() {
    // Check browser support
    if (!('serviceWorker' in navigator) || !('Notification' in window)) {
      return false;
    }

    try {
      // Register service worker
      this.registration = await navigator.serviceWorker.register(
        '/service-worker.js',
        { scope: '/' }
      );
      // Request notification permission
      if (Notification.permission === 'granted') {
        this.enabled = true;
        return await this.registerPushSubscription();
      } else if (Notification.permission !== 'denied') {
        const permission = await Notification.requestPermission();
        if (permission === 'granted') {
          this.enabled = true;
          return await this.registerPushSubscription();
        } else {
          return false;
        }
      }

      return false;
    } catch (error) {
      console.error('[PUSH] Initialization failed:', error);
      return false;
    }
  }

  /**
   * Register push subscription with backend
   */
  async registerPushSubscription() {
    try {
      if (!this.registration) {
        return false;
      }

      const subscription = await this.registration.pushManager.getSubscription();

      if (!subscription) {
        // For web push, we would need VAPID keys and server support
        // For now, we'll use Web Notifications API directly
        return true;
      }

      // If subscription exists, register it with backend
      const subscriptionJSON = subscription.toJSON();

      await api.post('/notifications/push-token', {
        token: JSON.stringify(subscriptionJSON),
        platform: 'web',
      });

      return true;
    } catch (error) {
      console.error('[PUSH] Failed to register subscription:', error);
      return true; // Still continue with Web Notifications API
    }
  }

  /**
   * Show a notification
   */
  showNotification(title, options = {}) {
    if (!this.enabled && Notification.permission !== 'granted') {
      return;
    }

    try {
      const notification = new Notification(title, {
        icon: '/kensafety-logo.png',
        badge: '/kensafety-badge.png',
        ...options,
      });

      // Handle notification click
      notification.onclick = () => {
        window.focus();
        notification.close();
        this.callListeners({
          type: 'click',
          title,
          ...options,
        });
      };

      notification.onclose = () => {
        this.callListeners({
          type: 'close',
          title,
          ...options,
        });
      };

      return notification;
    } catch (error) {
      console.error('[PUSH] Failed to show notification:', error);
    }
  }

  /**
   * Show alert notification
   */
  showAlertNotification(alert) {
    const { type, severity, siteId } = alert;
    const severity_emojis = {
      CRITICAL: '🚨',
      HIGH: '⚠️',
      MEDIUM: '⚡',
      LOW: 'ℹ️',
    };

    const title = `${severity_emojis[severity] || '🔔'} ${severity} Alert – ${type.replace(/_/g, ' ')}`;

    return this.showNotification(title, {
      body: alert.cameraCameraName
        ? `${alert.camera?.name || 'Camera'} at ${alert.site?.name || 'Site'} · ${Math.round(
            alert.confidence * 100
          )}% confidence`
        : `New ${severity.toLowerCase()} alert detected`,
      tag: `alert-${alert.id}`, // Prevents duplicate notifications
      requireInteraction: severity === 'CRITICAL',
      data: {
        alertId: alert.id,
        severity,
        type,
        siteId,
      },
    });
  }

  /**
   * Register listener for notifications
   */
  onNotification(callback) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(cb => cb !== callback);
    };
  }

  /**
   * Call all registered listeners
   */
  callListeners(notification) {
    this.listeners.forEach(callback => {
      try {
        callback(notification);
      } catch (error) {
        console.error('[PUSH] Listener error:', error);
      }
    });
  }

  /**
   * Request notification permission if needed
   */
  async requestPermission() {
    if (Notification.permission === 'granted') {
      return true;
    }

    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }

    return false;
  }

  /**
   * Check if notifications are enabled
   */
  isEnabled() {
    return Notification.permission === 'granted';
  }
}

export const pushNotificationService = new PushNotificationService();
export default pushNotificationService;
